import useUser from "@hooks/useUser";
import * as React from "react";
import { useHistory } from "react-router-dom";

import Button from "@salesforce/design-system-react/components/button";
import AlertModal from "@components/Modal/AlertModal";
import LocalStorage from "@services/localStorage";

export declare interface KrispcallInterface {
	isKrispcallShown: boolean;
	toggleKrispcall: React.Dispatch<React.SetStateAction<void>>;
	krispcallOpen: React.Dispatch<React.SetStateAction<void>>;
	krispcallClose: React.Dispatch<React.SetStateAction<void>>;
	dialNumber: (contact: string) => void;
	checkKrispcallProceed: (value: string, isExpired: boolean) => boolean;
	toggleDiallerLock: React.Dispatch<React.SetStateAction<void>>;
	dialerURL: string;
	isOptOut: boolean;
	isExpired: boolean;
	isDiallerLock: boolean;
}

export const KrispcallContext = React.createContext<KrispcallInterface | null>(
	null,
);

const KrispcallProvider = ({
	children,
}: {
	children: React.JSX.Element;
}): JSX.Element => {
	const [isKrispcallShown, setIsKrispcallShown] = React.useState(false);
	const [isOptOut, setIsOptOut] = React.useState(false);
	const [isExpired, setIsExpired] = React.useState(false);
	const [isAlertOpen, setIsAlertOpen] = React.useState(false);
	const [dialerURL, setDialerURL] = React.useState(
		process.env.KRISPCALL_URL as string,
	);
	const diallerLock = localStorage.getItem("isDiallerLock");
	const [isDiallerLock, setIsDiallerLock] = React.useState<boolean>(
		diallerLock ? JSON.parse(diallerLock) : false,
	);

	const {
		integrations,
		profile: { permissions },
		userType,
	} = useUser();

	const history = useHistory();

	const toggleKrispcall = (): void => {
		setIsKrispcallShown(st => !st);
	};

	const toggleDiallerLock = (): void => {
		LocalStorage.savePersonalization({ isDiallerLock: !isDiallerLock });
		setIsDiallerLock(st => !st);
	};

	const krispcallOpen = (): void => setIsKrispcallShown(true);

	const krispcallClose = (): void => setIsKrispcallShown(false);

	const toggleAlert = (): void => setIsAlertOpen(st => !st);

	const getKrispcallIntegrations = (): boolean =>
		!!integrations.find(
			each => each.provider === "KRISPCALL" && each.status === "ACTIVE",
		);

	const getTitle = (): string => {
		const hasKrispcallIntegration = getKrispcallIntegrations();

		if (!hasKrispcallIntegration) return "Integrate!";
		return "Contact admin!";
	};
	const getBody = (): string => {
		const hasKrispcallIntegration = getKrispcallIntegrations();

		if (!hasKrispcallIntegration || userType === "USER") {
			if (userType === "USER") {
				return "You have not integrated any voice call service yet. Contact your admin to proceed with the integration.";
			}
			return "You have not integrated any voice call service yet. Integrate to make or receive calls.";
		}

		return "You have not been provided with access to voice call services. Contact your admin for permissions.";
	};

	const getFooter = (): JSX.Element => {
		const hasKrispcallIntegration = getKrispcallIntegrations();
		if (!hasKrispcallIntegration && userType !== "USER") {
			return (
				<React.Fragment>
					<Button
						key="noBtn"
						id="NO"
						label="Cancel"
						onClick={toggleAlert}
						className="pc-modal-secondary-btn"
					/>
					<Button
						key="yesBtn"
						id="YES"
						label="Integrate"
						onClick={(): void => {
							setIsAlertOpen(false);
							history.push("/settings/integration/call");
						}}
						className="pc-modal-primary-btn"
					/>
				</React.Fragment>
			);
		}
		return (
			<React.Fragment>
				<Button
					key="okBtn"
					id="OK"
					label="OK"
					onClick={toggleAlert}
					className="pc-modal-primary-btn"
				/>
			</React.Fragment>
		);
	};

	const dialNumber = (contact: string): void => {
		setDialerURL(
			`${process.env.KRISPCALL_URL}/dialer?call=${contact}&sms=true&smsRedirection=false&platform=%27PepperCloud%27`,
		);
	};

	const checkKrispcallProceed = (val: string, isExpired: boolean): boolean => {
		const hasKrispcallIntegration = getKrispcallIntegrations();
		setIsExpired(isExpired);
		if (val === "Opt out") {
			setIsOptOut(true);
		} else {
			setIsOptOut(false);
		}
		if (!hasKrispcallIntegration || !permissions.ACTM.CREATE) {
			setIsAlertOpen(true);
			return false;
		}

		return true;
	};

	return (
		<KrispcallContext.Provider
			value={{
				isKrispcallShown,
				toggleKrispcall,
				krispcallOpen,
				krispcallClose,
				dialNumber,
				checkKrispcallProceed,
				toggleDiallerLock,
				dialerURL,
				isOptOut,
				isExpired,
				isDiallerLock,
			}}
		>
			{children}

			<AlertModal
				isOpen={isAlertOpen}
				toggleAlert={toggleAlert}
				iconProps={{
					name: "pc-info-circle",
					size: "large",
				}}
				title={getTitle()}
				footer={getFooter()}
			>
				{getBody()}
			</AlertModal>
		</KrispcallContext.Provider>
	);
};

export default KrispcallProvider;
